import { baseApi } from './baseApi';

export const purchaseApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    getPurchaseOrderNotes: builder.query({
      query: (params) => ({
        url: '/purchase/notes',
        params,
      }),
      providesTags: ['PurchaseOrderNote'],
    }),
    getPurchaseOrderNote: builder.query({
      query: (id) => `/purchase/notes/${id}`,
      providesTags: (result, error, id) => [{ type: 'PurchaseOrderNote', id }],
    }),
    createPurchaseOrderNote: builder.mutation({
      query: (data) => ({
        url: '/purchase/notes',
        method: 'POST',
        body: data,
      }),
      invalidatesTags: ['PurchaseOrderNote'],
    }),
    updatePurchaseOrderNote: builder.mutation({
      query: ({ id, ...data }) => ({
        url: `/purchase/notes/${id}`,
        method: 'PUT',
        body: data,
      }),
      invalidatesTags: (result, error, { id }) => ['PurchaseOrderNote', { type: 'PurchaseOrderNote', id }],
    }),
    updatePurchaseOrderNoteStatus: builder.mutation({
      query: ({ id, status }) => ({
        url: `/purchase/notes/${id}/status`,
        method: 'PUT',
        body: { status },
      }),
      invalidatesTags: (result, error, { id }) => ['PurchaseOrderNote', { type: 'PurchaseOrderNote', id }],
    }),
    deletePurchaseOrderNote: builder.mutation({
      query: (id) => ({
        url: `/purchase/notes/${id}`,
        method: 'DELETE',
      }),
      invalidatesTags: (result, error, id) => ['PurchaseOrderNote', { type: 'PurchaseOrderNote', id }],
    }),
    getPurchaseOrders: builder.query({
      query: (params) => ({
        url: '/purchase/orders',
        params,
      }),
      providesTags: ['Purchase'],
    }),
    getPurchaseOrder: builder.query({
      query: (id) => `/purchase/orders/${id}`,
      providesTags: (result, error, id) => [{ type: 'Purchase', id }],
    }),
    getPurchaseReturns: builder.query({
      query: (params) => ({
        url: '/purchase/returns',
        params,
      }),
      providesTags: ['PurchaseReturn'],
    }),
    getPurchaseReturn: builder.query({
      query: (id) => `/purchase/returns/${id}`,
      providesTags: (result, error, id) => [{ type: 'PurchaseReturn', id }],
    }),
    createPurchaseReturn: builder.mutation({
      query: (data) => ({
        url: '/purchase/returns',
        method: 'POST',
        body: data,
      }),
      invalidatesTags: ['PurchaseReturn', 'Supplier', 'Purchase', 'Finance', 'Reports', 'Account'],
    }),
    deletePurchaseReturn: builder.mutation({
      query: (id) => ({
        url: `/purchase/returns/${id}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['PurchaseReturn', 'Supplier', 'Purchase', 'Finance', 'Reports', 'Account'],
    }),
    createPurchaseOrder: builder.mutation({
      query: (data) => ({
        url: '/purchase/orders',
        method: 'POST',
        body: data,
      }),
      invalidatesTags: ['Purchase'],
    }),
    updatePurchaseOrderStatus: builder.mutation({
      query: ({ id, status }) => ({
        url: `/purchase/orders/${id}/status`,
        method: 'PUT',
        body: { status },
      }),
      invalidatesTags: (result, error, { id }) => ['Purchase', { type: 'Purchase', id }],
    }),
    deletePurchaseOrder: builder.mutation({
      query: (id) => ({
        url: `/purchase/orders/${id}`,
        method: 'DELETE',
      }),
      invalidatesTags: (result, error, id) => ['Purchase', { type: 'Purchase', id }],
    }),
    getPaymentAccounts: builder.query({
      query: () => '/purchase/payment-accounts',
      providesTags: ['Purchase'],
    }),
    getSuppliers: builder.query({
      query: (params) => ({
        url: '/purchase/suppliers',
        params,
      }),
      providesTags: ['Supplier'],
    }),
    getSupplier: builder.query({
      query: (id) => `/purchase/suppliers/${id}`,
      providesTags: (result, error, id) => [{ type: 'Supplier', id }],
    }),
    getSupplierHistory: builder.query({
      query: (id) => `/purchase/suppliers/${id}/history`,
      providesTags: (result, error, id) => [{ type: 'Supplier', id }],
    }),
    createSupplier: builder.mutation({
      query: (data) => ({
        url: '/purchase/suppliers',
        method: 'POST',
        body: data,
      }),
      invalidatesTags: ['Supplier'],
    }),
    updateSupplier: builder.mutation({
      query: ({ id, ...data }) => ({
        url: `/purchase/suppliers/${id}`,
        method: 'PUT',
        body: data,
      }),
      invalidatesTags: ['Supplier'],
    }),
    toggleSupplierStatus: builder.mutation({
      query: (id) => ({
        url: `/purchase/suppliers/${id}/status`,
        method: 'POST',
      }),
      invalidatesTags: ['Supplier'],
    }),
    createSupplierPayment: builder.mutation({
      query: ({ supplierId, ...data }) => ({
        url: `/purchase/suppliers/${supplierId}/payments`,
        method: 'POST',
        body: data,
      }),
      invalidatesTags: ['Supplier', 'Purchase', 'Finance', 'Reports', 'Account'],
    }),
    deleteSupplierPayment: builder.mutation({
      query: ({ supplierId, paymentId }) => ({
        url: `/purchase/suppliers/${supplierId}/payments/${paymentId}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['Supplier', 'Purchase', 'Finance', 'Reports', 'Account'],
    }),
    allocateSupplierAdvance: builder.mutation({
      query: ({ supplierId, amount }) => ({
        url: `/purchase/suppliers/${supplierId}/allocate-advance`,
        method: 'POST',
        body: { amount },
      }),
      invalidatesTags: ['Supplier', 'Purchase', 'Finance', 'Reports', 'Account'],
    }),
  }),
});

export const {
  useGetPurchaseOrderNotesQuery,
  useGetPurchaseOrderNoteQuery,
  useCreatePurchaseOrderNoteMutation,
  useUpdatePurchaseOrderNoteMutation,
  useUpdatePurchaseOrderNoteStatusMutation,
  useDeletePurchaseOrderNoteMutation,
  useGetPurchaseOrdersQuery,
  useGetPurchaseOrderQuery,
  useCreatePurchaseOrderMutation,
  useUpdatePurchaseOrderStatusMutation,
  useDeletePurchaseOrderMutation,
  useGetPurchaseReturnsQuery,
  useGetPurchaseReturnQuery,
  useCreatePurchaseReturnMutation,
  useDeletePurchaseReturnMutation,
  useGetPaymentAccountsQuery,
  useGetSuppliersQuery,
  useLazyGetSuppliersQuery,
  useGetSupplierQuery,
  useGetSupplierHistoryQuery,
  useCreateSupplierMutation,
  useUpdateSupplierMutation,
  useToggleSupplierStatusMutation,
  useCreateSupplierPaymentMutation,
  useDeleteSupplierPaymentMutation,
  useAllocateSupplierAdvanceMutation,
} = purchaseApi;
